import { reactive, computed, getCurrentInstance } from "vue";
import { useFilters } from "@/composables/filters/useFilters";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";

let instance = null;

export function useOperatingCurrenciesTableViewState() {
  if (instance) return instance;

  const filter = useFilters();
  const app = getCurrentInstance();
  const { proxy } = app || {};

  // Documented table baseline (Confluence OperatingCurrenciesTable / ER-10445).
  const DEFAULT_STATUS_FILTER = getDefaultFiltersForKey("operatingCurrenciesTable");
  const DEFAULT_RATE_MODE_FILTER = ["daily", "corporate"];

  const state = reactive({
    filters: {
      values: [...DEFAULT_STATUS_FILTER],
      searchText: "",
      searchGhostText: "Search currency",
      showFilterDialog: false,
      key: "operatingCurrenciesTable",
      clearSelection: "",
      statusOptions: ["active", "retired"],
      // Rate mode filter (ER-9960). Both modes selected = no rate-mode narrowing.
      rateModeValues: [...DEFAULT_RATE_MODE_FILTER],
    },
    filterGroups: [],
    loading: {
      table: false,
    },
    addCurrencyModal: {
      show: false,
      message: "",
    },
    editCurrencyModal: {
      show: false,
      message: "",
    },
    deleteCurrencyModal: {
      show: false,
      message: "",
    },
    table: {
      selectedCurrencies: [],
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      search: "",
    },
    currentCurrency: null,
  });

  const hasSelectedCurrencies = computed(
    () => state.table.selectedCurrencies.length > 0
  );

  const loadFiltersOnReload = () => {
    const savedFilters = filter.getFilters(state.filters.key);
    if (savedFilters && savedFilters.length > 0) {
      state.filters.values = savedFilters;
    } else {
      state.filters.values = [...DEFAULT_STATUS_FILTER];
    }
  };

  const applyFilters = async (filters, currencyComposable) => {
    const newArray = filters
      .map((item) => {
        if (item.value !== null) {
          if (typeof item === "string") {
            return item;
          } else if (
            typeof item === "object" &&
            // eslint-disable-next-line no-prototype-builtins
            item.hasOwnProperty("value")
          ) {
            return item.value;
          }
        }
      })
      .filter(Boolean);

    filter.setFilters({ key: state.filters.key, filters: newArray });
    state.filters.values = newArray;

    // Clear existing filters and apply new ones
    if (currencyComposable) {
      currencyComposable.clearCurrencyDynamicFilters();
      currencyComposable.setPaginationDataOffset(0);

      // Apply status filters
      state.filters.values.forEach((el) => {
        const lowerEl = typeof el === "string" ? el.toLowerCase() : el;
        if (lowerEl === "active" || lowerEl === "retired") {
          currencyComposable.addCurrencyFilter("status", lowerEl);
        }
      });

      // Re-apply search filters if they exist
      if (state.filters.searchText && state.filters.searchText.trim() !== "") {
        currencyComposable.addCurrencyFilter("name", state.filters.searchText);
      }

      state.filters.showFilterDialog = false;

      // Load currencies with new filters
      await currencyComposable.loadAllCurrencies();
    }
  };

  const handleCurrencySort = (sortData, currencyComposable) => {
    if (currencyComposable) {
      const finalSortField = sortData.descending
        ? `-${sortData.field}`
        : `${sortData.field}`;
      currencyComposable.setOrderingDataField(finalSortField);
      currencyComposable.loadAllCurrencies();
    }
  };

  const removeFilters = async (removedFilters, currencyComposable) => {
    if (currencyComposable) {
      currencyComposable.clearCurrencyDynamicFilters();
      currencyComposable.setPaginationDataOffset(0);
    }

    filter.setFilters({
      key: state.filters.key,
      filters: state.filters.values,
    });
    state.filters.clearSelection = removedFilters;

    await applyFilters(
      state.filters.values.map((value) => ({ label: value, value: value })),
      currencyComposable
    );
  };

  const removeAllFiltersAndReload = async (currencyComposable) => {
    if (currencyComposable) {
      currencyComposable.clearCurrencyDynamicFilters();
      currencyComposable.setPaginationDataOffset(0);
    }

    // Reset to documented default (Active), not an unfiltered view.
    filter.setFilters({
      key: state.filters.key,
      filters: [...DEFAULT_STATUS_FILTER],
    });
    state.filters.values = [...DEFAULT_STATUS_FILTER];
    state.filters.rateModeValues = [...DEFAULT_RATE_MODE_FILTER];
    state.filters.searchText = "";
    state.filters.searchGhostText = "Search currency";

    if (currencyComposable) {
      DEFAULT_STATUS_FILTER.forEach((status) => {
        currencyComposable.addCurrencyFilter("status", status);
      });
      await currencyComposable.loadAllCurrencies();
    }
  };

  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const performSearch = async (string, currencyComposable) => {
    if (currencyComposable) {
      currencyComposable.removeCurrencyFilter("name");
      currencyComposable.removeCurrencyFilter("code");

      if (string && string.trim() !== "") {
        currencyComposable.addCurrencyFilter("name", string);
      }

      currencyComposable.setPaginationDataOffset(0);
      state.filters.searchText = string;
      await currencyComposable.loadAllCurrencies();
    }
  };

  const debouncedSearch = debounce(performSearch, 500);

  const handleSearch = (string, currencyComposable) => {
    const queryString = string;
    debouncedSearch(queryString, currencyComposable);
  };

  const handleRequest = async ({ page, itemsPerPage }, currencyComposable) => {
    if (currencyComposable) {
      currencyComposable.setPaginationDataLimit(itemsPerPage);
      currencyComposable.setPaginationDataOffset((page - 1) * itemsPerPage);
      await currencyComposable.loadAllCurrencies();
    }
  };

  const clearSearch = (currencyComposable) => {
    state.filters.searchText = "";
    if (currencyComposable) {
      currencyComposable.removeCurrencyFilter("name");
      currencyComposable.removeCurrencyFilter("code");
    }
  };

  const handleCurrencyDropdownOperation = async (operation, currency) => {
    state.currentCurrency = currency;

    if (!operation || !currency) return;

    switch (operation.value) {
      case "edit_action":
        state.editCurrencyModal.show = true;
        break;
      case "delete_action":
        state.deleteCurrencyModal.message = `${currency.name} is about to be deleted. This action is irreversible.`;
        state.deleteCurrencyModal.show = true;
        break;
      case "activate_action":
        // Handle activate
        break;
      case "deactivate_action":
        // Handle deactivate
        break;
      default:
        break;
    }
  };

  const handleCurrencyOperation = async (
    operation,
    accountId,
    currencyComposable,
    payload = null
  ) => {
    try {
      if (proxy) {
        proxy.$loader.setAppLoading(true);
      }

      // Handle different currency operations
      switch (operation) {
        case "ADD_CURRENCY":
          // Add currency logic
          if (currencyComposable) {
            await currencyComposable.addCurrency({ accountId, data: payload });
            await currencyComposable.loadAllCurrencies();
          }
          state.addCurrencyModal.show = false;
          break;

        case "EDIT_CURRENCY":
          // Edit currency logic
          if (currencyComposable && state.currentCurrency) {
            await currencyComposable.updateCurrency({
              accountId,
              currencyId: state.currentCurrency.id,
              data: payload,
            });
            await currencyComposable.loadAllCurrencies();
          }
          state.editCurrencyModal.show = false;
          break;

        case "DELETE_CURRENCY":
          // Delete currency logic
          if (currencyComposable && state.currentCurrency) {
            await currencyComposable.deleteCurrency({
              accountId,
              currencyId: state.currentCurrency.id,
            });
            await currencyComposable.loadAllCurrencies();
          }
          state.deleteCurrencyModal.show = false;
          break;

        default:
          break;
      }

      state.currentCurrency = null;
    } catch (e) {
      console.error("error on currency operation: ", e);
      if (proxy && proxy.$notifications) {
        proxy.$notifications?.error("An error occurred while processing the currency operation.");
      }
    } finally {
      if (proxy) {
        proxy.$loader.setAppLoading(false);
      }
    }
  };

  const initializeFilters = async () => {
    const app = getCurrentInstance();
    const { proxy } = app || {};

    if (!proxy) {
      return;
    }

    try {
      // Initialize any feature flags if needed
    } catch (e) {
      console.error("Error in initializeFilters:", e);
    }
  };

  instance = {
    state,
    hasSelectedCurrencies,
    applyFilters,
    removeFilters,
    removeAllFiltersAndReload,
    handleCurrencySort,
    handleSearch,
    performSearch,
    handleRequest,
    clearSearch,
    handleCurrencyDropdownOperation,
    handleCurrencyOperation,
    initializeFilters,
    loadFiltersOnReload,
  };

  return instance;
}

export function resetOperatingCurrenciesTableViewState() {
  instance = null;
}
